/**
 * v3.ira — /v3/ira/* (Wave 5.2 IRA transactional verticals).
 *
 * Routes:
 *   POST /v3/ira/contributions  — eligibility-validated contribution post
 *   (sibling waves add /distributions, /rollovers, /rmd-withdrawals here)
 *
 * Server-side validation contract:
 *   plan-active → owner age vs catch-up → YTD vs limit → MAGI phaseout →
 *   IraTransactionCode catalog lookup → TaxPlnTranAdd post.
 *
 * Per Bill 2026-04-25 Q4/Q5 sign-off, the IraTransactionCode catalog ships
 * EMPTY at v3.0 launch — the runtime returns `code: 'TC_NOT_CONFIGURED'`
 * with a friendly admin-link prompt until Emily / IRA team seeds the rows
 * via the Admin → IRA → Transaction Codes editor.
 */

import type {
  RequestOptions,
  IraContributionRequest,
  IraContributionResponse,
  IraDistributionRequest,
  IraDistributionResponse,
  IraRolloverRequest,
  IraRolloverResponse,
  IraRmdPaymentRequest,
  IraRmdResponse,
  IraRecurringContributionRequest,
  IraRecurringResponse,
  IraTransferOutRequest,
  IraInHouseTransferRequest,
  IraTransferResponse,
  IraBeneficiaryAddRequest,
  IraBeneficiaryAddResponse,
  IraBeneficiariesListResponse,
} from '@retail-ops/shared-types';
import type { ApiHttpClient } from '../http.js';

export class IraApi {
  public constructor(private readonly http: ApiHttpClient) {}

  /**
   * POST /v3/ira/contributions — server-validated contribution post.
   *
   * The Function evaluates plan-active → age → YTD limit → MAGI phaseout →
   * TC scenario lookup → TaxPlnTranAdd. On success returns the running YTD,
   * remaining headroom, and (when applicable) a MAGI phaseout warning.
   *
   * Failure shapes carry a typed `code` (see `IraContributionErrorCode`)
   * so the wizard can render code-specific inline guidance — most notably
   * the `TC_NOT_CONFIGURED` admin-link banner when the catalog is empty.
   *
   * Caller MUST set `officerCode` on the body — the http transport also
   * forwards `x-pcb-officer-code` from the request context, but the body
   * field is authoritative when both are present.
   */
  public postContribution(
    req: IraContributionRequest,
    opts?: RequestOptions
  ): Promise<IraContributionResponse> {
    return this.http.post<IraContributionResponse>('/v3/ira/contributions', {
      ...opts,
      body: req,
      retry: false,
    });
  }

  /**
   * POST /v3/ira/distributions — server-validated distribution post (Wave 5.2
   * IRA-FN-DISTRIBUTIONS).
   *
   * Validation order on the server:
   *   plan-active → IraReasonCode lookup (must be active) → exception-doc gate
   *   → delivery validation → insufficient-balance guard → penalty-exempt
   *   detection (IraInstitutionRelationship) → withholding default fallback →
   *   IraTransactionCode scenario lookup → server-side withholding compute →
   *   TaxPlnTranAdd post → SessionEvent `ira.distribution.posted`.
   *
   * The success payload carries the canonical 1099-R box code, computed
   * fed/state withholding amounts, and net amount — DistributionFlow.tsx
   * renders these in the confirmation summary directly without recomputing
   * client-side. Failure shapes carry a typed `code` (see
   * `IraDistributionErrorCode`) so the wizard can render code-specific
   * inline guidance — most notably `TC_NOT_CONFIGURED` (admin-link banner)
   * and `MISSING_EXCEPTION_DOCS` (officer must confirm supporting documentation).
   */
  public postDistribution(
    req: IraDistributionRequest,
    opts?: RequestOptions
  ): Promise<IraDistributionResponse> {
    return this.http.post<IraDistributionResponse>('/v3/ira/distributions', {
      ...opts,
      body: req,
      retry: false,
    });
  }

  /**
   * POST /v3/ira/rollovers — compliance-validated rollover post (Wave 5.2
   * IRA-FN-ROLLOVERS, port-plan §3.2.8).
   *
   * Server-side validation pipeline:
   *   plan-active → expire stale prior pending indirect rollovers (so the
   *   12-month lookback doesn't over-reject) → §408(d)(3)(B) one-rollover-
   *   per-12-months query (per OwnerCifId, only 60_DAY_INDIRECT subject) →
   *   SIMPLE 2-year wait (when destination=SIMPLE & source=Trad &
   *   sourcePlanCreatedAt < 730d ago) → IraTransactionCodes scenario
   *   resolution (catalog ships EMPTY → 422 TC_NOT_CONFIGURED) →
   *   IraRolloverEvent ledger insert (BEFORE Core post for compliance
   *   durability) → TaxPlnTranAdd (TC 614 direct / 632 indirect, but read
   *   from catalog) → 1099-R box-7 stamping via IraReasonCode lookup →
   *   5498 receiving-year stamp → SessionEvent `ira.rollover.initiated`.
   *
   * The success payload carries the IraRolloverEvent.eventId, the resolved
   * TC, the 60-day deadline (when indirect), and the 1099-R box-7 code.
   * RolloverFlow.tsx renders these in the confirmation summary directly
   * without recomputing client-side.
   *
   * Failure shapes carry a typed `code` (see `IraRolloverErrorCode`) so the
   * wizard can render code-specific inline guidance — most notably:
   *   ONE_ROLLOVER_LIMIT_EXCEEDED — 12-month rule banner pointing to
   *                                 prior rollover history
   *   SIMPLE_2YR_NOT_MET           — 2-year participation banner
   *   60_DAY_DEADLINE_PASSED       — past-deadline finalization warning
   *   TC_NOT_CONFIGURED            — admin-link banner (catalog empty)
   */
  public postRollover(
    req: IraRolloverRequest,
    opts?: RequestOptions
  ): Promise<IraRolloverResponse> {
    return this.http.post<IraRolloverResponse>('/v3/ira/rollovers', {
      ...opts,
      body: req,
      retry: false,
    });
  }

  /**
   * POST /v3/ira/rmd-payments — server-validated Required Minimum Distribution
   * post (Wave 5.2 IRA-FN-RMD, port-plan §3.2.9 + §7.4).
   *
   * The Function recomputes the RMD server-side from the prior-year-end
   * balance and the IraRmdFactor row keyed by (age, UniformLifetime,
   * effectiveYear). The officer's `calcAmount` must reconcile within $0.50;
   * otherwise the post is rejected with `RMD_AMOUNT_MISMATCH` and the
   * response carries both `serverCalcAmount` + `officerCalcAmount` for the
   * UI to render side-by-side.
   *
   * Mode validation (server enforces, client renders the rejection inline):
   *   FULL_RMD — distributionAmount must equal serverCalcAmount ± $0.50
   *   PARTIAL  — distributionAmount must be < serverCalcAmount AND ≥ 10% of it
   *              (rejects $1 token partials with `RMD_PARTIAL_BELOW_MINIMUM`)
   *   OVER_RMD — distributionAmount must be ≥ serverCalcAmount
   *
   * SECURE 2.0 cohort handling: the server resolves the cohort start age
   * from the owner's birth-year (admin-configurable via SystemSettings;
   * defaults per port-plan §7.4 — pre-1949→70½, 1949-1950→72, 1951-1959→73,
   * 1960+→75). When the owner is below the cohort start age the post
   * returns `RMD_NOT_DUE` with a friendly cohort explanation that cites the
   * birth year + the year the owner first becomes RMD-required.
   *
   * Other failure codes (see `IraRmdErrorCode`):
   *   • OWNER_DOB_MISSING        — Core CIF lacks DOB; admin must remediate
   *   • RMD_FACTOR_NOT_SEEDED    — IraRmdFactor row missing for (age, year)
   *   • PRIOR_YEAR_BALANCE_MISSING — Core didn't surface prior-YE balance
   *   • TC_NOT_CONFIGURED        — admin must seed RMD_PAYMENT scenario
   */
  public postRmd(
    req: IraRmdPaymentRequest,
    opts?: RequestOptions
  ): Promise<IraRmdResponse> {
    return this.http.post<IraRmdResponse>('/v3/ira/rmd-payments', {
      ...opts,
      body: req,
      retry: false,
    });
  }

  /**
   * POST /v3/ira/post-recurring-contribution — persist a standing instruction
   * for periodic IRA contributions (Wave 5.3 IRA-FE-RECURRING).
   *
   * The endpoint persists an IraRecurringSchedule row and computes the first
   * NextRunDate from (cadence, startDate). It does NOT fire jXRL today —
   * the Wave 5.4 Timer-trigger sweeps active schedules whose NextRunDate
   * is on or before UTC today and posts the actual contribution via the
   * existing /v3/ira/contributions pipeline.
   *
   * Failure shapes carry a typed `code` (see `IraRecurringErrorCode`) so the
   * wizard can render code-specific inline guidance — most notably:
   *   • INVALID_CADENCE / INVALID_START_DATE / INVALID_END_DATE
   *   • MISSING_ACH_DETAILS / MISSING_INTERNAL_ACCOUNT
   *   • INVALID_CONTRIBUTION_KIND
   */
  public postRecurringContribution(
    req: IraRecurringContributionRequest,
    opts?: RequestOptions
  ): Promise<IraRecurringResponse> {
    return this.http.post<IraRecurringResponse>(
      '/v3/ira/post-recurring-contribution',
      {
        ...opts,
        body: req,
        retry: false,
      }
    );
  }

  /**
   * POST /v3/ira/post-transfer-out — outbound trustee-to-trustee transfer
   * (Wave 5.3 IRA-FE-TRANSFERS-AND-BENEFMOD).
   *
   * Server-side responsibilities:
   *   • Penalty-exempt detection via the Q3 admin-config'd
   *     `IraInstitutionRelationship` catalog. When `destinationInstitution.code`
   *     matches a row with `penaltyExempt=true` (Raymond James / Verna seeded),
   *     the response carries `penaltyApplies=false` + a friendly
   *     `penaltyExemptionReason` cribbed from `Notes`.
   *   • TC sequence resolution from the Q4 admin-config'd `IraTransactionCode`
   *     catalog (scenario `TRANSFER_OUT`). Empty catalog → 422 with
   *     `code='TC_NOT_CONFIGURED'` and `userVisible=true` so the wizard can
   *     render an inline "contact admin" banner — NOT a 500.
   */
  public postTransferOut(
    req: IraTransferOutRequest,
    opts?: RequestOptions
  ): Promise<IraTransferResponse> {
    return this.http.post<IraTransferResponse>('/v3/ira/post-transfer-out', {
      ...opts,
      body: req,
      retry: false,
    });
  }

  /**
   * POST /v3/ira/post-transfer-internal — PCB plan → PCB plan internal
   * transfer (Wave 5.3 IRA-FE-TRANSFERS-AND-BENEFMOD).
   *
   * Same admin-config'd TC sequence pattern as transfer-out (scenario
   * `IN_HOUSE_TRANSFER`) — friendly-fail when the catalog is empty. The
   * server enforces source != destination.
   */
  public postInHouseTransfer(
    req: IraInHouseTransferRequest,
    opts?: RequestOptions
  ): Promise<IraTransferResponse> {
    return this.http.post<IraTransferResponse>(
      '/v3/ira/post-transfer-internal',
      {
        ...opts,
        body: req,
        retry: false,
      }
    );
  }

  /**
   * GET /v3/ira/beneficiaries?planAccountNumber=XXX — normalized list of
   * existing beneficiaries via TaxPlnBenfSrch passthrough. The Function
   * never exposes Core's nested envelope; the response shape is
   * `{ planAccountNumber, beneficiaries: IraBeneficiaryRow[] }`.
   */
  public listBeneficiaries(
    planAccountNumber: string,
    opts?: RequestOptions
  ): Promise<IraBeneficiariesListResponse> {
    return this.http.get<IraBeneficiariesListResponse>(
      '/v3/ira/beneficiaries',
      {
        ...opts,
        query: { planAccountNumber },
      }
    );
  }

  /**
   * POST /v3/ira/post-beneficiary — ADD-only path (Q2 acceptable defer).
   *
   * Wave 5.3 contract:
   *   • Percent-on-the-wire is decimal 0.0–1.0 per Q1 sign-off (UI may
   *     present whole-percent UX but MUST convert before sending).
   *   • EDIT path stays locked pending the JHA TaxPlnBenfMod schema RFI —
   *     the BeneficiaryEditor renders an info banner with the
   *     "set percent to 0% (deactivate) + add replacement" workaround.
   *   • Type field is one of INDIVIDUAL | TRUST | ESTATE | CHARITY; the
   *     server enforces type-specific field requirements.
   */
  public postBeneficiary(
    req: IraBeneficiaryAddRequest,
    opts?: RequestOptions
  ): Promise<IraBeneficiaryAddResponse> {
    return this.http.post<IraBeneficiaryAddResponse>(
      '/v3/ira/post-beneficiary',
      {
        ...opts,
        body: req,
        retry: false,
      }
    );
  }
}
